import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import tw from 'twin.macro';

import {
  CategoryTitle,
  ChartsWrapper,
  Header,
  NoDataAvailable,
  OuterChartsWrapper,
  RiskLevelLegend,
  RiskLevelLegendItem,
  Title,
  Wrapper,
} from './TrendingStyledComponents';
import { BrandSafetyPieChart } from './BrandSafetyPieChart';
import { useOverviewData } from '@app/hooks/useOverviewData';
import { Loader } from '@components/Loader';
import {
  GARM_RISK_LEVEL_COLOR_MAPPER,
  useOverviewContext,
} from '@insights_module/context/OverviewContext';

type ChartData = {
  category: string;
  data: {
    name: string;
    value: number;
    fill: string;
  }[];
}[];

export const GarmCategoriesBrakeChart = () => {
  const { t } = useTranslation();
  const { garmPerCategoryOverview, episodesCount, isLoadingBrandSafety } = useOverviewData();
  const { selectedBrandSafetyCategory, setSelectedBrandSafetyCategory } = useOverviewContext();

  const chartData: ChartData = useMemo(() => {
    if (!garmPerCategoryOverview || !episodesCount) return [];

    return Object.entries(garmPerCategoryOverview || {}).map(([category, value]) => {
      return {
        category,
        data: Object.entries(value).map(([riskLevel, value]) => ({
          name: riskLevel,
          value,
          fill: GARM_RISK_LEVEL_COLOR_MAPPER[
            riskLevel as keyof typeof GARM_RISK_LEVEL_COLOR_MAPPER
          ],
        })),
      };
    });
  }, [garmPerCategoryOverview, episodesCount]);

  useEffect(() => {
    // if there is no selected category or previously selected category is not in the new data, select the first one
    if (
      selectedBrandSafetyCategory &&
      chartData.find(({ category }) => category === selectedBrandSafetyCategory)
    )
      return;
    setSelectedBrandSafetyCategory(chartData[0]?.category || '');
  }, [chartData, setSelectedBrandSafetyCategory, selectedBrandSafetyCategory]);

  return (
    <Wrapper tw='h-auto justify-start'>
      <Header tw='flex-col items-start'>
        <div tw='flex flex-col xl:flex-row items-start xl:items-center justify-between w-full'>
          <Title>{t('insights.overview_page.brand_safety_section.garm_brake_by_category')}</Title>
          <RiskLevelLegend>
            {Object.entries(GARM_RISK_LEVEL_COLOR_MAPPER).map(([riskLevel, color]) => (
              <RiskLevelLegendItem key={riskLevel}>
                <div tw='w-[16px] h-[16px] rounded-[2px]' css={{ backgroundColor: color }} />
                <div>
                  {t(
                    `insights.overview_page.brand_safety_section.garm_analysis_chart_levels.${riskLevel}`
                  )}
                </div>
              </RiskLevelLegendItem>
            ))}
          </RiskLevelLegend>
        </div>
        <div tw='text-[12px] text-muted'>
          {t('insights.overview_page.brand_safety_section.category_break_subtitle')}
        </div>
      </Header>

      {isLoadingBrandSafety ? (
        <Loader size='md' theme='dark' />
      ) : (
        <OuterChartsWrapper>
          {!chartData.length ? (
            <NoDataAvailable>{t('insights.overview_page.no_data')}</NoDataAvailable>
          ) : (
            chartData.map(({ category, data }, index) => (
              <ChartsWrapper
                key={category}
                style={{ zIndex: `${chartData.length - index}` }}
                css={[selectedBrandSafetyCategory === category && tw`bg-gray-2`]}
                onClick={() => setSelectedBrandSafetyCategory(category)}>
                <BrandSafetyPieChart data={data} />
                <CategoryTitle>{t(`insights.garm_category.${category}`)}</CategoryTitle>
              </ChartsWrapper>
            ))
          )}
        </OuterChartsWrapper>
      )}
    </Wrapper>
  );
};
